import { useState } from 'react';
import { useStudentData } from '@/hooks/useStudentData';
import { formatDate, getWeekday } from '@/lib/date';

function extractVideoEmbedUrl(url: string): string | null {
  if (!url) return null;

  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;

  const gdMatch = url.match(/drive\.google\.com\/file\/d\/([\w-]+)/);
  if (gdMatch) return `https://drive.google.com/file/d/${gdMatch[1]}/preview`;

  const vmMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vmMatch) return `https://player.vimeo.com/video/${vmMatch[1]}`;

  if (url.endsWith('.mp4') || url.includes('video')) return url;

  return url;
}

export default function StudentKayitlar() {
  const { group, records, loading, error, refetch } = useStudentData();
  const [activeVideo, setActiveVideo] = useState<string | null>(null);
  const [activeRecordTitle, setActiveRecordTitle] = useState<string>('');

  const playVideo = (url: string | null, title: string) => {
    if (!url) return;
    setActiveVideo(extractVideoEmbedUrl(url));
    setActiveRecordTitle(title);
  };

  const closeVideo = () => {
    setActiveVideo(null);
    setActiveRecordTitle('');
  };

  if (loading) {
    return (
      <div className="mx-auto flex max-w-5xl flex-col items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
        <p className="mt-4 text-sm text-foreground-500">Kayıtlar yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-5xl py-10">
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <i className="ri-error-warning-line text-2xl text-red-500" />
          <p className="mt-2 text-sm font-medium text-red-700">Kayıtlar yüklenirken bir hata oluştu.</p>
          <p className="mt-1 text-xs text-red-500">{error}</p>
          <button type="button" onClick={refetch} className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer">
            <i className="ri-refresh-line" /> Yeniden Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Ders Kayıtlarım</h1>
        <p className="mt-1 text-sm text-foreground-600">Geçmiş ders kayıtlarınızı buradan izleyebilirsiniz.</p>
      </div>

      {activeVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-4xl overflow-hidden rounded-xl bg-black shadow-2xl">
            <div className="flex items-center justify-between bg-foreground-950 px-4 py-3">
              <h3 className="text-sm font-semibold text-white truncate pr-4">{activeRecordTitle}</h3>
              <button type="button" onClick={closeVideo} className="text-white/70 hover:text-white transition cursor-pointer">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="aspect-video w-full">
              <iframe
                src={activeVideo}
                title={activeRecordTitle}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="h-full w-full"
              />
            </div>
          </div>
        </div>
      )}

      {!group ? (
        <div className="rounded-xl border border-background-200 bg-white p-10 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
            <i className="ri-vidicon-line text-2xl text-foreground-400" />
          </div>
          <p className="text-sm font-semibold text-foreground-700">Henüz size atanmış bir grup bulunmuyor.</p>
          <p className="mt-1 text-xs text-foreground-500">Ders içerikleriniz tanımlandığında burada görünecek.</p>
        </div>
      ) : records.length === 0 ? (
        <div className="rounded-xl border border-background-200 bg-white p-10 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
            <i className="ri-vidicon-line text-2xl text-foreground-400" />
          </div>
          <p className="text-sm font-semibold text-foreground-700">Henüz size atanmış bir ders kaydı bulunmuyor.</p>
          <p className="mt-1 text-xs text-foreground-500">Öğretmeniniz kayıt eklediğinde burada görünecek.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {records.map((r) => (
            <div
              key={r.id}
              className="group relative overflow-hidden rounded-xl border border-background-200 bg-white transition hover:shadow-md"
            >
              <div className="relative aspect-video bg-gradient-to-br from-primary-900 to-primary-700 flex items-center justify-center">
                <div className="text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm transition group-hover:scale-110">
                    <i className="ri-play-fill text-2xl text-white" />
                  </div>
                  {r.duration && (
                    <span className="absolute bottom-2 right-2 rounded-md bg-black/60 px-2 py-0.5 text-xs font-semibold text-white">
                      {r.duration}
                    </span>
                  )}
                </div>
              </div>

              <div className="p-4 space-y-3">
                <h3 className="font-semibold text-foreground-950 text-sm leading-snug line-clamp-2">{r.title}</h3>
                {r.description && (
                  <p className="text-xs text-foreground-600 line-clamp-2">{r.description}</p>
                )}
                <div className="flex items-center justify-between text-xs text-foreground-500">
                  <span>
                    {r.date ? `${formatDate(r.date)} · ${getWeekday(r.date)}` : 'Tarih yok'}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line" /> {r.views} görüntülenme
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => playVideo(r.video_url, r.title)}
                  disabled={!r.video_url}
                  className={`mt-1 w-full rounded-lg py-2 text-sm font-semibold transition cursor-pointer ${
                    r.video_url
                      ? 'bg-primary-500 text-white hover:bg-primary-600'
                      : 'bg-background-100 text-foreground-400 cursor-not-allowed'
                  }`}
                >
                  <i className="ri-play-circle-line mr-1" />
                  {r.video_url ? 'İzle' : 'Video Yok'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}